import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { pickingListsApi } from "../../../api/inventory/picking_lists";
import { getUsersAsync } from "../../../slices/user";

export function PickingListReminderSettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const usersState = useSelector((s) => s.users);
  const [selected, setSelected] = useState([]);
  const [savedIds, setSavedIds] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(
      getUsersAsync({
        filters: [],
        page: 1,
        per_page: 200,
        query: "",
        sort: "asc",
        sortBy: "name",
        view: "all",
        fields: "id,name,displayTitle,email",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSettingsLoading(true);
      try {
        const res = await pickingListsApi.getPickingSettings();
        const ids = res?.data?.data?.picking_list_reminder_user_ids || [];
        if (!cancelled) setSavedIds(Array.isArray(ids) ? ids.map(Number) : []);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setSavedIds([]);
          toast.error(t("inventory.picking.settings.load_error"));
        }
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    const users = usersState?.data || [];
    if (!users.length || savedIds === null) return;
    const map = new Map(users.map((u) => [u.id, u]));
    setSelected(savedIds.map((id) => map.get(id)).filter(Boolean));
  }, [usersState.data, savedIds]);

  const options = usersState?.data || [];

  const onSave = async () => {
    setSaving(true);
    try {
      const ids = selected.map((u) => u.id);
      const res = await pickingListsApi.updatePickingSettings(ids);
      if (res?.data?.success) {
        setSavedIds(ids);
        toast.success(t("inventory.picking.settings.saved"));
      } else {
        toast.error(t("inventory.picking.settings.save_error"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("inventory.picking.settings.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const loadingUsers = Boolean(usersState?.isLoading);
  const disabled = settingsLoading || loadingUsers;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
      <Typography variant="h6" className="mb-1 font-semibold text-gray-900">
        {t("inventory.picking.settings.title")}
      </Typography>
      <Typography variant="body2" className="mb-4 text-gray-600">
        {t("inventory.picking.settings.description")}
      </Typography>
      <Autocomplete
        multiple
        options={options}
        value={selected}
        onChange={(_, v) => setSelected(v)}
        getOptionLabel={(o) => o.displayTitle || o.name || String(o.id)}
        loading={disabled}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("inventory.picking.settings.recipients")}
            placeholder={t("inventory.picking.settings.recipients_placeholder")}
          />
        )}
      />
      <div className="mt-4 flex justify-end">
        <Button variant="contained" onClick={onSave} disabled={saving || disabled}>
          {saving ? t("inventory.picking.settings.saving") : t("inventory.picking.settings.save")}
        </Button>
      </div>
    </div>
  );
}
